import React, { useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { productApi, type ProductDTO } from '../api/kfc';

const SEP = '; ';

/** 产品展示：中文 / 英文（缺一则只显示有的一侧） */
export function formatProductCargoLabel(p: ProductDTO): string {
  const zn = p.nameZN?.trim();
  const en = p.nameEN?.trim();
  if (zn && en) return `${zn} / ${en}`;
  return zn || en || '';
}

export function parseCargoNameTags(cargoName?: string | null): string[] {
  if (!cargoName || !cargoName.trim()) return [];
  return cargoName
    .split(/[;；]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function joinCargoNameTags(tags?: string[] | null): string {
  if (!tags?.length) return '';
  return tags.map((t) => t.trim()).filter(Boolean).join(SEP);
}

interface Props {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * 货物品名：可从产品管理多选（展示中英文名），也可直接输入标签。
 * Form 侧用 getValueProps / getValueFromEvent 与字符串 cargoName 互转。
 */
const CargoNameSelect: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder = '选择产品或直接输入品名，可多选',
}) => {
  const [products, setProducts] = useState<ProductDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    productApi
      .list({ view: 'summary' })
      .then((res) => {
        if (!cancelled) setProducts(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const seen = new Set<string>();
    const list: { label: string; value: string }[] = [];
    for (const p of products) {
      const label = formatProductCargoLabel(p);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      list.push({ label, value: label });
    }
    return list;
  }, [products]);

  return (
    <Select
      mode="tags"
      allowClear
      showSearch
      disabled={disabled}
      loading={loading}
      placeholder={placeholder}
      value={value}
      onChange={(v) => onChange?.(v as string[])}
      options={options}
      optionFilterProp="label"
      tokenSeparators={[';']}
      maxTagCount="responsive"
      notFoundContent={loading ? <Spin size="small" /> : '无匹配产品，可直接输入'}
      style={{ width: '100%' }}
    />
  );
};

/** Form.Item 与字符串 cargoName 互转的 props */
export const cargoNameFormItemProps = {
  getValueFromEvent: (tags: string[]) => joinCargoNameTags(tags),
  getValueProps: (v: string | undefined) => ({ value: parseCargoNameTags(v) }),
};

export default CargoNameSelect;
